import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Fragment as _Fragment, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, vModelText as _vModelText, renderList as _renderList, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "input-icon--left" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  class: "base-input--label"
}
const _hoisted_4 = ["for"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["value", "id", "aria-haspopup", "aria-expanded", "aria-controls", "aria-required", "aria-activedescendant"]
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = {
  key: 1,
  class: "input-icon--right"
}
const _hoisted_9 = { class: "row h-100 mh-100" }
const _hoisted_10 = { class: "col align-self-center" }
const _hoisted_11 = { class: "input-icon" }
const _hoisted_12 = { class: "input-icon--dropdown" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = ["id", "aria-selected", "onClick"]
const _hoisted_15 = ["innerHTML"]
const _hoisted_16 = ["innerHTML"]

import {
  reactive,
  computed,
  ref,
  watch,
  nextTick, useId,
} from 'vue';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectField',
  props: {
  modelValue: {},
  valueKey: {
    type: String,
    default: () => 'value',
    required: false,
  },
  search: {
    type: Boolean,
    default: () => false,
  },
  name: {
    type: String,
    required: false,
    default: () => null,
  },
  label: {
    type: String,
    required: false,
    default: () => '&nbsp;',
  },
  options: {
    type: Array,
    required: false,
    default: () => [],
  },
  error: {
    type: [String, Object],
    required: false,
    default: () => '',
  },
  showValidCheck: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  iconLeft: {
    type: [String, Object],
    required: false,
    default: () => null,
  },
  borderless: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  rounded: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  hideLabel: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  hideSelectedOption: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  fixSelectedOption: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  borderRight: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  isRequired: {
    type: Boolean,
    required: false,
    default: () => false,
  },
},
  emits: [
  'update:modelValue',
  'input',
],
  setup(__props, { emit: __emit }) {

library.add(faCheck);
library.add(faChevronDown);
library.add(faSearch);

const emit = __emit;

const props = __props;
const root = ref();
const searchInput = ref();
const mainInput = ref();
const uniqId = useId();

const uniqInputId = `${uniqId}-input-`;
const listboxId = `${uniqId}-listbox`;

const component = reactive({
  id: `sf-${Math.random()
    .toString(36)
    .substring(2, 11)}`,
  error: props.error || '',
  open: false,
  searchFor: null as string | null,
  activeIndex: -1,
  isFocused: false,
});

const hasError = computed(() => {
  const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
  const $error = compError.length ? compError : props.error;

  return $error ? $error.length > 0 : false;
});

const errorMessage = computed(() => {
  if (hasError.value) {
    const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
    const $error = compError.length ? compError : props.error;

    return Array.isArray($error)
      ? $error[0]
      : $error;
  }

  return '';
});

const value = computed({
  get(): unknown {
    return props.modelValue;
  },
  set(newValue: unknown): void {
    emit('update:modelValue', newValue);
  },
});

const getOptionValue = ($option: string | number | Record<string, unknown>): string | number => {
  if (typeof $option === 'object') {
    return $option[props.valueKey] as string | number;
  }

  return $option;
};

const getOptionLabel = ($option: string | number | Record<string, unknown>) => {
  if (typeof $option === 'object') {
    return $option.label;
  }

  return $option;
};

const getOptionKey = ($option: string | number | Record<string, unknown>) => {
  let $label = $option as string;

  if (typeof $option === 'object') {
    $label = $option.label as string;
  }

  return `${$label}`.replace(/[\W_]+/g, ' ');
};

const searchedOptions = computed(() => {
  if (component.searchFor !== null) {
    return [...props.options].filter(($option) => {
      const $optionVal = getOptionValue($option as string | Record<string, string>);
      const $optionLabel = getOptionLabel($option as string | Record<string, string>);

      const valueIncludesSearch = `${$optionVal}`.toLowerCase()
        .includes((component.searchFor || '')
          .toLowerCase());
      const labelIncludesSearch = `${$optionLabel}`.toLowerCase()
        .includes((component.searchFor || '')
          .toLowerCase());
      return valueIncludesSearch || labelIncludesSearch;
    });
  }

  return [...props.options];
});

const filled = computed(() => (props.options || []).filter((option) => (typeof option === 'object' ? (option as Record<string, any>)[props.valueKey] : option) === value.value).length);

const selectedLabel = computed(() => {
  let $label = null;

  const $selectedOption = [...props.options].filter(($option) => `${getOptionValue($option as string | Record<string, string>)}` === `${value.value}`);

  if ($selectedOption.length) {
    $label = getOptionLabel($selectedOption[0] as string | Record<string, string>);
  }

  return $label;
});

const setListStyles = () => {
  if (root.value) {
    const rootPosition = root.value.getBoundingClientRect();
    const body = document.querySelector('body');

    // eslint-disable-next-line no-unused-expressions
    body?.style.setProperty('--select-options-list-top', `${rootPosition.bottom}px`);
    // eslint-disable-next-line no-unused-expressions
    body?.style.setProperty('--select-options-list-left', `${rootPosition.left}px`);
    // eslint-disable-next-line no-unused-expressions
    body?.style.setProperty('--select-options-list-width', `${rootPosition.width}px`);
  }

  window.addEventListener('scroll', setListStyles);
};

const removeListStyles = () => {
  const body = document.querySelector('body');

  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-top');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-left');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-width');

  window.removeEventListener('scroll', setListStyles);
};

const scrollToActive = () => {
  nextTick(() => {
    const list = document.getElementById(listboxId);
    if (list) {
      const activeOption = list.querySelector('.active');
      if (activeOption) {
        activeOption.scrollIntoView({ block: 'nearest' });
      }
    }
  });
};

const openSelect = () => {
  if (props.options.length > 0) {
    setListStyles();
    nextTick(() => {
      component.open = true;
      // Set active index to currently selected option
      const selectedIndex = searchedOptions.value.findIndex(($option) => `${getOptionValue($option as string | Record<string, string>)}` === `${value.value}`);
      component.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
      scrollToActive();
    });
  }
};

const ifClickedOutside = (event: MouseEvent) => {
  if (root.value) {
    const withinRootBoundaries = event.composedPath().includes(root.value);
    const withinSearchBoundaries = searchInput.value ? event.composedPath().includes(searchInput.value) : false;

    const withinBoundaries = withinRootBoundaries || withinSearchBoundaries;
    if (!withinBoundaries) {
      component.open = false;
      removeListStyles();
      component.searchFor = null;
    }
  }
};

const getAllFocusableElements = () => {
  const elements = Array.from(document.querySelectorAll('a, button, input, select, textarea, [tabindex]')) as HTMLElement[];
  const listbox = document.getElementById(listboxId);

  return elements.filter((el) => {
    const isInsideListbox = listbox ? listbox.contains(el) : false;
    return !el.hasAttribute('disabled') && el.tabIndex >= 0 && !isInsideListbox;
  });
};

const findNextFocusableElement = (): HTMLElement | undefined => {
  if (!root.value) return undefined;

  const focusableElements = getAllFocusableElements();
  const inputElement = root.value.querySelector('input');
  const currentIndex = inputElement ? focusableElements.indexOf(inputElement as HTMLElement) : -1;

  if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
    return focusableElements[currentIndex + 1];
  }

  return undefined;
};

const findPreviousFocusableElement = (): HTMLElement | undefined => {
  if (!root.value) return undefined;

  const focusableElements = getAllFocusableElements();
  const inputElement = root.value.querySelector('input');
  const currentIndex = inputElement ? focusableElements.indexOf(inputElement as HTMLElement) : -1;

  if (currentIndex > 0) {
    return focusableElements[currentIndex - 1];
  }

  return undefined;
};

const setValue = ($option: any, shouldMoveToNext: boolean | MouseEvent = false) => {
  value.value = getOptionValue($option);
  component.open = false;
  removeListStyles();
  component.searchFor = null;

  nextTick(() => {
    if (shouldMoveToNext === true) {
      const nextElementToFocus = findNextFocusableElement();
      if (nextElementToFocus) {
        nextElementToFocus.focus();
        return;
      }
    }
    mainInput.value?.focus();
  });
};

const onFocusOutSearch = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    event.preventDefault();
    const nextElementToFocus = event.shiftKey ? findPreviousFocusableElement() : findNextFocusableElement();

    if (nextElementToFocus) {
      event.preventDefault();

      nextElementToFocus.focus();
    }

    setTimeout(() => {
      component.open = false;
    }, 0);
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  if (props.options.length === 0) return;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      if (!component.open) {
        openSelect();
      } else {
        component.activeIndex = (component.activeIndex + 1) % searchedOptions.value.length;
        scrollToActive();
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (!component.open) {
        openSelect();
      } else {
        component.activeIndex = (component.activeIndex - 1 + searchedOptions.value.length) % searchedOptions.value.length;
        scrollToActive();
      }
      break;
    case 'Enter':
    case ' ':
      if (event.key === ' ' && component.search) return; // Allow space in search input
      event.preventDefault();
      if (!component.open) {
        openSelect();
      } else if (component.activeIndex >= 0) {
        setValue(searchedOptions.value[component.activeIndex] as string | Record<string, string>, event.key === 'Enter');
      }
      break;
    case 'Escape':
      if (component.open) {
        event.preventDefault();
        component.open = false;
        removeListStyles();
      }
      break;
    default:
      break;
  }
};

watch(() => component.searchFor, () => {
  if (component.open) {
    component.activeIndex = 0;
  }
});

watch(() => component.open, ($isOpen) => {
  if ($isOpen) {
    document.addEventListener('click', ifClickedOutside);
    setTimeout(() => {
      if (searchInput.value) {
        searchInput.value.focus();
      }
    }, 50);
  } else {
    document.removeEventListener('click', ifClickedOutside);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["select-field-wrapper fs-regular", {
        open: component.open,
        'hide-selected': __props.hideSelectedOption,
        'fix-selected': __props.fixSelectedOption,
      }]),
    ref_key: "root",
    ref: root
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["base-input-field base-input-field--select", {
          invalid: hasError.value,
          valid: !hasError.value && __props.showValidCheck,
          borderless: __props.borderless,
          rounded: __props.rounded,
          filled: filled.value,
          focused: component.open || component.isFocused,
          'icon-left': __props.iconLeft,
          'label-hidden': __props.hideLabel,
        }]),
      onClick: openSelect,
      onFocusin: _cache[0] || (_cache[0] = ($event: any) => (component.isFocused = true)),
      onFocusout: _cache[1] || (_cache[1] = ($event: any) => (component.isFocused = false))
    }, [
      (__props.iconLeft)
        ? (_openBlock(), _createElementBlock("div", { key: __props.iconLeft }, [
            _createElementVNode("span", _hoisted_1, [
              (typeof __props.iconLeft === 'object')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (__props.iconLeft.icon)
                      ? (_openBlock(), _createElementBlock("i", {
                          key: 0,
                          class: _normalizeClass(["fa-fw icon", __props.iconLeft.icon])
                        }, null, 2))
                      : (__props.iconLeft.svg)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 1,
                            class: "icon-svg",
                            innerHTML: __props.iconLeft.svg
                          }, null, 8, _hoisted_2))
                        : (__props.iconLeft.image)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 2,
                              class: "image",
                              style: _normalizeStyle({ backgroundImage: `url(${__props.iconLeft.image.url || __props.iconLeft.image})` })
                            }, null, 4))
                          : _createCommentVNode("", true)
                  ], 64))
                : _createCommentVNode("", true),
              (typeof __props.iconLeft === 'string')
                ? (_openBlock(), _createElementBlock("i", {
                    key: 1,
                    class: _normalizeClass(["fa-fw icon", __props.iconLeft])
                  }, null, 2))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(["col-main", {
            nobrd: ! __props.showValidCheck && __props.options.length === 0,
            'border-right': __props.borderRight,
          }])
      }, [
        _createElementVNode("div", null, [
          (!__props.hideLabel)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("label", {
                  for: uniqInputId + component.id
                }, _toDisplayString(__props.label), 9, _hoisted_4),
                _renderSlot(_ctx.$slots, "tooltip")
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass(["input-label text-start", __props.disabled ? 'disabled' : null]),
            innerHTML: selectedLabel.value || ' '
          }, null, 10, _hoisted_5),
          _createElementVNode("input", {
            ref_key: "mainInput",
            ref: mainInput,
            class: "base-input__input",
            type: "text",
            readonly: "",
            value: selectedLabel.value,
            id: uniqInputId + component.id,
            role: "combobox",
            "aria-autocomplete": "list",
            "aria-haspopup": __props.options.length > 0 ? 'listbox' : undefined,
            "aria-expanded": component.open,
            "aria-controls": __props.options.length > 0 ? listboxId : undefined,
            "aria-required": __props.isRequired ? 'true' : undefined,
            "aria-activedescendant": component.activeIndex >= 0 ? _unref(uniqId) + '-option-' + component.activeIndex : undefined,
            onKeydown: onKeyDown
          }, null, 40, _hoisted_6),
          (hasError.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "base-input-field__error-message fs-small",
                innerHTML: errorMessage.value
              }, null, 8, _hoisted_7))
            : _createCommentVNode("", true)
        ])
      ], 2),
      (!hasError.value && __props.showValidCheck)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_unref(FontAwesomeIcon), { icon: "fa-light fa-check" })
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", _hoisted_11, [
        _createElementVNode("span", _hoisted_12, [
          _createVNode(_unref(FontAwesomeIcon), { icon: "fa-light fa-chevron-down" })
        ])
      ], 512), [
        [_vShow, __props.options.length > 0]
      ])
    ], 34),
    (component.open)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          _createElementVNode("div", {
            id: listboxId,
            class: _normalizeClass(["select-field-list font-regular", { 'search': __props.search }])
          }, [
            (__props.search)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _withDirectives(_createElementVNode("input", {
                    type: "text",
                    ref_key: "searchInput",
                    ref: searchInput,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((component.searchFor) = $event)),
                    "aria-label": "Suche...",
                    onKeydown: _cache[3] || (_cache[3] = ($event: any) => {onKeyDown($event); onFocusOutSearch($event)})
                  }, null, 544), [
                    [_vModelText, component.searchFor]
                  ]),
                  _createVNode(_unref(FontAwesomeIcon), { icon: "fa-regular fa-search" })
                ]))
              : _createCommentVNode("", true),
            (_openBlock(), _createElementBlock("ul", {
              key: `optionsList-${searchedOptions.value.length}`,
              role: "listbox",
              tabindex: "-1"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchedOptions.value, (option, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  id: _unref(uniqId) + '-option-' + index,
                  key: getOptionKey(option),
                  class: _normalizeClass({
                'selected': getOptionValue(option) === value.value,
                'active': component.activeIndex === index
              }),
                  role: "option",
                  "aria-selected": getOptionValue(option) === value.value,
                  tabindex: "-1",
                  onMousedown: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
                  onClick: ($event: any) => (setValue(option))
                }, [
                  (typeof option === 'object')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (option.icon)
                          ? (_openBlock(), _createElementBlock("i", {
                              key: 0,
                              class: _normalizeClass(["fa-fw icon", option.icon])
                            }, null, 2))
                          : (option.svg)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: "icon-svg",
                                innerHTML: option.svg
                              }, null, 8, _hoisted_15))
                            : (option.image)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 2,
                                  class: "image",
                                  style: _normalizeStyle({ backgroundImage: `url(${option.image.url || option.image})` })
                                }, null, 4))
                              : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    innerHTML: getOptionLabel(option)
                  }, null, 8, _hoisted_16)
                ], 42, _hoisted_14))
              }), 128))
            ]))
          ], 2)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})